/**
 * Whether a rising value is good news. Drives delta colour only — the arrow
 * always follows the sign, and the text carries the value, so colour is never
 * the sole signal.
 */
export type MetricPolarity = 'higher_is_better' | 'lower_is_better' | 'neutral';

export type DeltaTone = 'positive' | 'negative' | 'neutral';

export function deltaTone(delta: number | null | undefined, polarity: MetricPolarity): DeltaTone {
  if (typeof delta !== 'number' || !Number.isFinite(delta) || delta === 0) return 'neutral';
  if (polarity === 'neutral') return 'neutral';
  const rising = delta > 0;
  return (polarity === 'higher_is_better') === rising ? 'positive' : 'negative';
}

/**
 * Operational stats by API key; anything not listed is neutral (volume
 * figures such as orders or kg requested are context, not a score).
 */
const OPERATIONAL_STAT_POLARITY: Record<string, MetricPolarity> = {
  completion_rate: 'higher_is_better',
  completed_orders: 'higher_is_better',
  completed_waste_amount: 'higher_is_better',
  waste_diversion_rate: 'higher_is_better',
  sustainable_supply: 'higher_is_better',
  total_revenue: 'higher_is_better',
  cancellation_rate: 'lower_is_better',
  cancelled_orders: 'lower_is_better',
  full_percentage: 'lower_is_better',
  containers_needing_attention: 'lower_is_better',
};

export function operationalStatPolarity(stat: string): MetricPolarity {
  return OPERATIONAL_STAT_POLARITY[normalizeMetricKey(stat)] ?? 'neutral';
}

/** Minimal shape of a KPI limit (see `stores/kpi.store.ts`). */
export interface KpiLimitLike {
  name: string;
  isEnabled: boolean;
}

/** `TOTAL_ORDERS`, `total_orders`, `Total Orders` → `total_orders`. */
export function normalizeMetricKey(key: string): string {
  return key
    .trim()
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

/**
 * Enabled KPI limit for a metric. Matches on the canonical metric key so it
 * works in every UI language (titles are translated, keys are not).
 */
export function findEnabledKpiLimit<T extends KpiLimitLike>(
  limits: readonly T[],
  metricKey: string | null | undefined,
): T | undefined {
  if (!metricKey) return undefined;
  const wanted = normalizeMetricKey(metricKey);
  return limits.find((l) => l.isEnabled && normalizeMetricKey(l.name) === wanted);
}
